import type { SYSActions } from "./"
import { SYSConfig, ovl } from "./"
import { SYSValidatorsFunctions } from "./appdefformvalidation"
import { AppDefPermission } from "./config"

import { _registerComponents } from "./global/globals"
import { handleScreenVisible } from "./helpers"
import { validatorsFunctions } from "./library/forms/helper"
import type { _ScreenState } from "./library/sysbaseelement"
import { hooks } from "./library/table/tablerowheadermenu/tablesettings/allactions"
let SYSTableSettings = { hooks }

import { defineElements } from "./registercomponents"
import { actionTracking } from "./tracker/proxyhandler"

const interceptorAsyncFn = (beforeFn, afterFn, originalFn, key) => {
  return async (value) => {
    let execution = {
      handled: false,
      currentRes: undefined,
      canceled: false,
      cancel: function () {
        this.canceled = true
        this.handled = true
      },
      data: {},
    }
    try {
      actionTracking.actionRunning = true
      actionTracking.canceled = false
      let res
      if (beforeFn) {
        actionTracking.lastActionName = `${key}_Before`
        res = await beforeFn(value, {
          execution,
          global: {
            getState: () => ovl.getState(),
            getActions: () => ovl.getActions(),
          },
        })
      }
      if (originalFn && !execution.handled) {
        actionTracking.lastActionName = key
        let res2 = await originalFn(value, {
          execution,
          global: {
            getState: () => ovl.getState(),
            getActions: () => ovl.getActions(),
          },
        })
        if (res !== undefined) {
          res = Object.assign(res, res2)
        } else {
          res = res2
        }
      }
      if (afterFn && !execution.handled) {
        actionTracking.lastActionName = `${key}_After`
        execution.currentRes = res
        let res3 = await afterFn(value, {
          execution,
          global: {
            getState: () => ovl.getState(),
            getActions: () => ovl.getActions(),
          },
        })
        if (res !== undefined) {
          res = Object.assign(res, res3)
        } else {
          res = res3
        }
      }
      return res
    } finally {
      actionTracking.canceled = execution.canceled
      actionTracking.actionRunning = false
    }
  }
}

const interceptorFn = (beforeFn, afterFn, originalFn, key) => {
  return (value) => {
    let execution = {
      handled: false,
      currentRes: undefined,
      canceled: false,
      cancel: () => {
        //@ts-ignore
        this.canceled = true
        //@ts-ignore
        this.handled = true
      },
      data: {},
    }
    try {
      actionTracking.actionRunning = true
      actionTracking.canceled = false
      let res
      if (beforeFn) {
        actionTracking.lastActionName = `${key}_Before`
        res = beforeFn(value, {
          execution,
          global: {
            getState: () => ovl.getState(),
            getActions: () => ovl.getActions(),
          },
        })
      }
      if (originalFn && !execution.handled) {
        actionTracking.lastActionName = key
        let res2 = originalFn(value, {
          execution,
          global: {
            getState: () => ovl.getState(),
            getActions: () => ovl.getActions(),
          },
        })
        if (res !== undefined) {
          res = Object.assign(res, res2)
        } else {
          res = res2
        }
      }
      if (afterFn && !execution.handled) {
        actionTracking.lastActionName = `${key}_After`
        execution.currentRes = res
        let res3 = afterFn(value, {
          execution,
          global: {
            getState: () => ovl.getState(),
            getActions: () => ovl.getActions(),
          },
        })
        if (res !== undefined) {
          res = Object.assign(res, res3)
        } else {
          res = res3
        }
      }
      return res
    } finally {
      actionTracking.canceled = execution.canceled
      actionTracking.actionRunning = false
    }
  }
}

const injectActions = (obj) =>
  Object.keys(obj).forEach((item) => {
    if (typeof obj[item] === "function") {
      // check if there are "Before" Actions without Main Action then switch it to Main Action
      let chk = item.toString()
      if (chk.endsWith("_Before")) {
        let chk2 = chk.substring(0, chk.length - 7)
        if (!obj[chk2]) {
          // there is a Before Fn but no Main Fn
          // so make the BeforeFn the Main Function
          obj[chk2] = obj[item]
          delete obj[item]
          item = chk2
        }
      } else if (chk.endsWith("_After")) {
        let chk2 = chk.substring(0, chk.length - 6)
        if (!obj[chk2]) {
          // there is a After Fn but no Main Fn
          // so make the AfterFn the Main Function
          obj[chk2] = obj[item]
          delete obj[item]
          item = chk2
        }
      }

      injectFn(obj, item)
    } else if (typeof obj[item] === "object") {
      injectActions(obj[item])
    }
  })

export const injectFn = (obj, item) => {
  let fnname = item.toString()
  if (fnname.endsWith("_Before") || fnname.endsWith("_After")) {
    return
  }
  let isAsync = obj[item].toString().startsWith("async")
  let beforeFn = undefined
  let afterFn = undefined

  if (obj[`${item}_Before`]) {
    beforeFn = obj[`${item}_Before`]
  }
  if (obj[`${item}_After`]) {
    afterFn = obj[`${item}_After`]
  }

  if (isAsync) {
    obj[item] = interceptorAsyncFn(beforeFn, afterFn, obj[item], item)
  } else {
    obj[item] = interceptorFn(beforeFn, afterFn, obj[item], item)
  }
}

// prepare screens state
//@ts-ignore
export const init = (
  appState,
  appActions,
  appScreens,
  appDialogs,
  appLongRunningProcess,

  ovlState,
  ovlActions,

  baseScreens,
  baseDialogs,
  baseLongRunningProcess,
  forms,
  baseForms,
  appEditors,
  baseEditors,
  appValidators,
  sysComponentsActions
) => {
  // sanitize SYSConfig
  if (SYSConfig.offline === undefined) {
    SYSConfig.offline = { enabled: false }
  }
  if (SYSConfig.translation === undefined) {
    SYSConfig.translation = { useLocal: false, showOnlyKeys: false }
  }
  if (SYSConfig.fetch === undefined) {
    SYSConfig.fetch = {}
  }
  SYSConfig.app.tableDefIds["SYSTableSettings_FilterSort"] = {
    getData: (state) =>
      state.ovl.features.SYSTableSettings.tables.SYSTableSettings_FilterSort,
  }
  SYSConfig.app.tableDefIds["SYSTableSettings_ColumnFilter"] = {
    getData: (state) =>
      state.ovl.features.SYSTableSettings.tables.SYSTableSettings_ColumnFilter,
  }

  SYSConfig.app.tableDefIds["SYSTranslation"] = {
    getData: (state) => state.ovl.features.SYSTranslation.tables.SYSTranslation,
  }

  SYSConfig.app.tableDefIds["SYSEPT"] = {
    getData: (state) => state.ovl.features.SYSEPT.tables.SYSEPT,
  }

  SYSConfig.app.tableDefIds["SYSPortalSettings"] = {
    getData: (state) =>
      state.ovl.features.SYSPortalSettings.tables.SYSPortalSettings,
  }

  SYSConfig.app.tableDefIds["SYSAudit"] = {
    getData: (state) => state.ovl.features.SYSAudit.tables.SYSAudit,
  }

  SYSConfig.app.tableDefIds["SYSUserAdministration_User"] = {
    getData: (state) =>
      state.ovl.features.SYSUserAdministration.tables
        .SYSUserAdministration_User,
  }

  SYSConfig.app.tableDefIds["SYSUserAdministration_UserRoleTransfer"] = {
    getData: (state) =>
      state.ovl.features.SYSUserAdministration.tables
        .SYSUserAdministration_User,
  }

  SYSConfig.app.tableDefIds["SYSUserAdministration_Role"] = {
    getData: (state) =>
      state.ovl.features.SYSUserAdministration.tables
        .SYSUserAdministration_Role,
  }

  SYSConfig.app.tableDefIds["SYSUserAdministration_Permission"] = {
    getData: (state) =>
      state.ovl.features.SYSUserAdministration.tables
        .SYSUserAdministration_Permission,
  }

  SYSConfig.app.tableDefIds["SYSUserAdministration_ResultingPermission"] = {
    getData: (state) =>
      state.ovl.features.SYSUserAdministration.tables
        .SYSUserAdministration_Permission,
  }

  SYSConfig.app.tableDefIds["SYSUserAdministration_UserRole"] = {
    getData: (state) =>
      state.ovl.features.SYSUserAdministration.tables
        .SYSUserAdministration_UserRole,
  }

  SYSConfig.app.tableDefIds["SYSUserAdministration_Company"] = {
    getData: (state) =>
      state.ovl.features.SYSUserAdministration.tables
        .SYSUserAdministration_Company,
  }

  /* Permission Administration */
  ;(SYSConfig.app.permissions["SYSPA"] = <AppDefPermission>{
    useModifiers: false,
  }),
    /* User Administration */
    (SYSConfig.app.permissions["SYSUA"] = <AppDefPermission>{
      useModifiers: true,
    }),
    /* Role Administration */
    (SYSConfig.app.permissions["SYSRA"] = <AppDefPermission>{
      useModifiers: true,
    }),
    /* Company Administration */
    (SYSConfig.app.permissions["SYSCA"] = <AppDefPermission>{
      useModifiers: true,
    }),
    /* User Role Administration */
    (SYSConfig.app.permissions["SYSUR"] = <AppDefPermission>{
      useModifiers: true,
    }),
    /* Audit */
    (SYSConfig.app.permissions["SYSAU"] = <AppDefPermission>{
      useModifiers: false,
    }),
    /* Translation */
    (SYSConfig.app.permissions["SYSTR"] = <AppDefPermission>{
      useModifiers: true,
    }),
    /* TableSettings */
    (SYSConfig.app.permissions["SYSTS"] = <AppDefPermission>{
      useModifiers: false,
    }),
    /* PortalSettings */
    (SYSConfig.app.permissions["SYSST"] = <AppDefPermission>{
      useModifiers: true,
    })

  if (!SYSConfig._system.appId) {
    SYSConfig._system.appId = "001"
  }

  // make sure its always 3 digits
  SYSConfig._system.appId = SYSConfig._system.appId.padStart(3, "0")

  if (!SYSConfig._system.defaultDateTimeControlSubmitType) {
    SYSConfig._system.defaultDateTimeControlSubmitType = "UTC"
  }

  let _state: any = { ovl: ovlState, app: appState }
  _state.ovl.screens.screens = {}

  Object.keys(appValidators).forEach((k) => {
    validatorsFunctions[k] = { type: "Custom" }
    //   let defaults: ValidatorOptions = appValidators[k]
    //   if (defaults === undefined) {
    //     defaults = { validation: { displayType: "WhenTouched" } }
    //   }
    //   validationDefaults.validators[k] = defaults
  })

  Object.keys(SYSValidatorsFunctions).forEach((k) => {
    validatorsFunctions[k] = { fn: SYSValidatorsFunctions[k], type: "SYS" }
  })

  Object.keys(appScreens).forEach((k) => {
    _state.ovl.screens.screens[k] = {
      screenCloseBehaviour: appScreens[k].screenCloseBehaviour,
      noHistory: appScreens[k].noHistory,
      callNavigateInHookOnRehydrate:
        appScreens[k].callNavigateInHookOnRehydrate,
      instance: {},
    }
  })

  Object.keys(baseScreens).forEach((k) => {
    _state.ovl.screens.screens[k] = {
      screenCloseBehaviour: baseScreens[k].screenCloseBehaviour,
      noHistory: baseScreens[k].noHistory,
      instance: {},
    }
  })

  _state.ovl.forms = {}

  Object.keys(forms)
    .concat(Object.keys(baseForms))
    .concat(Object.keys(baseEditors))
    .concat(Object.keys(appEditors))
    .forEach((k) => {
      _state.ovl.forms[k] = {}
    })

  // prepare dialogs state
  _state.ovl.dialogs = {}
  Object.keys(appDialogs)
    .concat(Object.keys(baseDialogs))
    .concat(Object.keys(baseEditors))
    .concat(Object.keys(appEditors))
    .forEach((k) => {
      _state.ovl.dialogs[k] = {
        rehydratable:
          appDialogs[k]?.rehydratable === undefined ||
          appDialogs[k]?.rehydratable === true,
        instance: {},
      }
    })

  // prepare longRunningProcess state
  _state.ovl.longRunningProcessTracker = {}
  Object.keys(baseLongRunningProcess)
    .concat()
    //.concat(Object.keys(baseLongRunningProcess))
    .forEach((k) => {
      _state.ovl.longRunningProcessTracker[k] = {
        snack: baseLongRunningProcess[k].snack,
        snackFixedByAppDef: !!baseLongRunningProcess[k].snack,
        lockScreen: baseLongRunningProcess[k].lockScreen,
        instance: {},
        isRefresh: baseLongRunningProcess[k].isRefresh,
      }
      // we already need an default instance here elsewise ProcessIsRunning will not track rendercomp correctly
      _state.ovl.longRunningProcessTracker[k].instance[k] = { isRunning: false }
    })

  Object.keys(appLongRunningProcess)
    //.concat(Object.keys(appLongRunningProcess))
    .forEach((k) => {
      _state.ovl.longRunningProcessTracker[k] = {
        snack: appLongRunningProcess[k].snack,
        snackFixedByAppDef: !!appLongRunningProcess[k].snack,
        lockScreen: appLongRunningProcess[k].lockScreen,
        instance: {},
        isRefresh: appLongRunningProcess[k].isRefresh,
      }
      // we already need an default instance here elsewise ProcessIsRunning will not track rendercomp correctly
      _state.ovl.longRunningProcessTracker[k].instance[k] = { isRunning: false }
    })

  Object.keys(appActions.features).forEach((f) => {
    if (!appActions.features[f].hooks) {
      appActions.features[f].hooks = {}
    }
    if (!appActions.features[f].actions) {
      appActions.features[f].actions = {}
    }
  })

  Object.keys(SYSConfig.app.screens).forEach((f) => {
    if (!appActions.screens[f]) {
      appActions.screens[f] = { hooks: {}, actions: {} }
    } else {
      if (!appActions.screens[f].hooks) {
        appActions.screens[f].hooks = {}
      }
      if (!appActions.screens[f].actions) {
        appActions.screens[f].actions = {}
      }
    }
  })

  sysComponentsActions.features.SYSTableSettings = SYSTableSettings
  // if there are syscomponentFeatures with same key as appFeatures merge em
  Object.keys(sysComponentsActions.features).forEach((s) => {
    if (appActions.features[s]) {
      appActions.features[s].hooks = Object.assign(
        appActions.features[s].hooks,
        sysComponentsActions.features[s].hooks
      )
      appActions.features[s].actions = Object.assign(
        appActions.features[s].actions,
        sysComponentsActions.features[s].actions
      )
    } else {
      appActions.features[s] = sysComponentsActions.features[s]
    }
  })
  //same for screens
  Object.keys(sysComponentsActions.screens).forEach((s) => {
    if (appActions.screens[s]) {
      appActions.screens[s].hooks = Object.assign(
        appActions.screens[s].hooks,
        sysComponentsActions.screens[s].hooks
      )
      appActions.screens[s].actions = Object.assign(
        appActions.screens[s].actions,
        sysComponentsActions.screens[s].actions
      )
    } else {
      appActions.screens[s] = sysComponentsActions.screens[s]
    }
  })

  let _actions: any = {}
  _actions.ovl = ovlActions
  _actions.app = appActions
  // find the functions in actions and inject our own action caller
  injectActions(_actions)

  return {
    state: _state,
    actions: <SYSActions>_actions,
  }
}

export const defineSYSElements = () => {
  defineElements()
}

export const registerElements = () => {
  let screenState = ovl.getState().ovl.screens.screens
  let dialogState = ovl.getState().ovl.dialogs
  //sheet.innerHTML =
  _registerComponents._customElementList.forEach((m, i) => {
    try {
      customElements.define(m.tagName, m.comp)
      if (m.screen) {
        // also register it as a screen
        screenState[m.screen].tag = m.tagName
      } else if (m.dialog) {
        // also register it as a dialog
        dialogState[m.dialog].tag = m.tagName
      }
    } catch (err) {
      console.error(`error registering ${m.tagName}: ${err}`)
    }
  })

  _registerComponents._customElementList = []
}

// export const addDefaultReactors = () => {
//   let screensState = ovl.getState().ovl.screens.screens
//   Object.keys(screensState).forEach((screenKey) => {
//     AddGlobalReactor(
//       [`ovl.screens.screens.${screenKey}.visible`],
//       handleScreenVisible
//     )
//   })
// }
